import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import MatchCard from "../../components/cards/match.card";
import { ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useState } from "react";

const KoMatchesComponent = ({ matches, participants, refresh }) => {
  const location = useLocation();
  const tournament = useMemo(
    () => location.state.tournament || {},
    [location.state.tournament],
  );
  const nodeTypes = useMemo(() => ({ matchNode: MatchCard }), []);
  const [nodes, setNodes] = useState([]);
  const [lines, setLines] = useState([]);
  const proOptions = useMemo(() => ({ hideAttribution: true }), []);

  const getMaxLevel = (matches) => {
    return Math.max(...matches.map((item) => item.level));
  };

  const getMatchesOfLevel = (matches, level) => {
    return matches.filter((m) => m.level === level);
  };

  const calculatePositions = (data, matchHeight) => {
    var positions = {};
    const xShiftPerLevel = 250;
    const levels = {};

    data.forEach((match) => {
      const level = match.level;
      if (!levels[level]) {
        levels[level] = [];
      }
      levels[level].push(match);
    });

    Object.keys(levels)
      .sort((a, b) => a - b)
      .forEach((level) => {
        const matches = levels[level];
        const x = level * xShiftPerLevel;

        if (level === "0") {
          matches.forEach((match, i) => {
            const y = 0 + i * matchHeight;
            positions[match.id] = { x, y };
          });
        } else {
          matches.forEach((match) => {
            const previousMatches = match.previousMatches;
            if (previousMatches && previousMatches.length > 0) {
              const avgY =
                previousMatches.reduce(
                  (sum, prevId) => sum + positions[prevId].y,
                  0,
                ) / previousMatches.length;
              positions[String(match.id)] = { x, y: avgY };
            }
          });
        }
      });
    return positions;
  };

  const generateNodes = useCallback((matches, participants, tournament) => {
    const basicHeight = 16;
    const entryHeight = 28;
    const cardHeight =
      tournament.participantsPerMatch * entryHeight + basicHeight + 20;
    let positions = calculatePositions(matches, cardHeight);
    let maxLevel = getMaxLevel(matches);
    let newNodes = [];

    for (let i = 0; i <= maxLevel; i++) {
      let matchesOfLevel = getMatchesOfLevel(matches, i);
      for (let mi = 0; mi < matchesOfLevel.length; mi++) {
        newNodes.push({
          id: String(matchesOfLevel[mi].id),
          type: "matchNode",
          data: {
            match: matchesOfLevel[mi],
            participants: participants,
            tournamentId: tournament.id,
            refresh: refresh,
          },
          position: {
            x: positions[matchesOfLevel[mi].id].x,
            y: positions[matchesOfLevel[mi].id].y,
          },
        });
      }
    }
    return newNodes;
  }, []);

  const generateLines = useCallback((matches) => {
    let newLines = [];
    for (let i = 0; i < matches.length; i++) {
      if (matches[i].followingMatches) {
        for (let j = 0; j < matches[i].followingMatches.length; j++) {
          newLines.push({
            id: `e${i}-${j}`,
            source: String(matches[i].id),
            target: String(matches[i].followingMatches[j]),
            type: "step",
          });
        }
      }
    }
    return newLines;
  }, []);

  useEffect(() => {
    if (matches.length > 0 && participants.length > 0) {
      const newNodes = generateNodes(matches, participants, tournament);
      const newLines = generateLines(matches);
      setNodes(newNodes);
      setLines(newLines);
    } else {
      setNodes([]);
      setLines([]);
    }
  }, [
    matches,
    participants,
    tournament,
    refresh,
    generateNodes,
    generateLines,
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "600px",
        minWidth: 0,
        position: "relative",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={lines}
        nodeTypes={nodeTypes}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        panOnDrag={false}
        preventScrolling={false}
        fitView={false}
        proOptions={proOptions}
      />
    </div>
  );
};

export default KoMatchesComponent;
